import { useState, useEffect } from 'react';
import { Box, Typography, Grid, TextField, InputAdornment, IconButton } from '@mui/material';
import { DataGridPro, GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-pro';
import { useSelector, useDispatch } from '../../../redux/store';
import Iconify from '../../../reusable-components/iconify';
import { getCategoryData } from '../../../redux/slices/rentals';
import DeleteRentalCategory from './DeleteRentalCategory';
import AddRentalCategory from './AddRentalCategory';
//import EditRentalCategory from './EditRentalCategory';
import { ScopeGuard, useScopeCheck } from '../../../reusable-components/scopes';

export default function RentalCategoriesView() {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const {
    data: { categoryData },
  } = useSelector((state) => state.rentals);

  const handleSearch = (event) => {
    const { value } = event.target;
    setSearchValue(value);
  }

  useEffect(() => {
    const uniqueCategoriesMap = {};
    categoryData?.forEach((item) => {
      if (item.categoryId && !uniqueCategoriesMap[item.categoryId]) {
      uniqueCategoriesMap[item.categoryId] = {
        id: item.categoryId,
        name: item.categoryName,
        icon: item.categoryIcon,
      };
      }
    });
    setCategories(Object.values(uniqueCategoriesMap));
    setSubcategories(
      categoryData
      ?.map((item) => ({
        id: item.subcategoryId,
        mainCategoryId: item.categoryId,
        name: item.subcategoryName,
      }))
      .filter(
        (sub) =>
        sub.id &&
        sub.name 
      ) ?? []
    );
    const transformedData = transformData(categories, subcategories);
    setData(transformedData);
  }, [categoryData]);

  useEffect(() => {
    const transformedData = transformData(categories, subcategories);
    setData(transformedData);
  }, [categories, subcategories]);

   const transformData = (categories, subcategories) => {
    const transformed = [];
    categories.forEach((category) => {
      transformed.push({
        id: category.id.toString(),
        category: category.name,
        hierarchy: [category.name],
        name: category.name,
        icon: category.icon,
      });

      subcategories
        .filter((sub) => sub.mainCategoryId === category.id)
        .forEach((sub) => {
          transformed.push({
            id: `${category.id}-${sub.id}`,
            category: category.name,
            hierarchy: [category.name, sub.name],
            mainCategoryId: category.id,
            name: sub.name,
            subId: sub.id,
            icon: category.icon,
          });
        });
    });

    return transformed.sort((a, b) => a.category.localeCompare(b.category));
  };

  const editSettings = useScopeCheck(['Rental-Settings-Edit'], true);

  useEffect(() => {
    dispatch(getCategoryData());
  }, [dispatch]);

  const columns = [
      {
        field: 'category',
        headerName: 'Category',
        width: 250,
        renderCell: (params) => (
          <>
            <Iconify
              icon={params.row.icon}
              width={params.row.mainCategoryId ? 25 : 40}
              height={params.row.mainCategoryId ? 25 : 40}
            />{' '}
            <span>
              <Typography variant="subtitle2" sx={{ ml: params.row.mainCategoryId ? 3 : 1 }}>
                {params.value}
              </Typography>
            </span>
          </>
        ),
      },
      {
        field: GRID_TREE_DATA_GROUPING_FIELD,
        width: 300,
      },
      editSettings &&
      {
        field: 'actions',
        headerName: 'Actions',
        disableReorder: true,
        width: 150,
        flex: 1,
        renderCell: (params) => (
          <>
            {/* TODO: enable edit when emoji picker fixed */}
            {/* <EditRentalCategory row={params.row} categories={categories} /> */}
            <DeleteRentalCategory
              row={params.row}
            />
          </>
        ),
        sortable: false,
        filterable: false,
      },
    ];

  return (
    <Box>
      <Grid container direction="row" alignItems="center" sx={{ mb: 1, ml: 1, mt: 1 }} spacing={1}>
        <ScopeGuard scopes = {['Rental-Settings-Edit']} allowAdmin>
        <Grid item>
          <AddRentalCategory description={'Category'} />
        </Grid>
        <Grid item>
          <AddRentalCategory description={'Subcategory'} />
        </Grid>
        </ScopeGuard>
        <Grid item sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            value={searchValue}
            onChange={handleSearch}
            size="small"
            placeholder="Search..."
            sx={{ mr: 2 }}
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    sx={{ display: searchValue === '' ? 'none' : 'inline-flex' }}
                    onClick={() => {
                      setSearchValue('');
                      handleSearch({ target: { value: '' } });
                    }}
                  >
                    <Iconify
                      icon={'ri:close-fill'}
                      sx={{
                        cursor: 'pointer',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
              style: { height: '30px', borderRadius: '4px', border: `solid 1px #404042` },
            }}
          />
        </Grid>
      </Grid>
      <DataGridPro
        treeData
        getTreeDataPath={(row) => row.hierarchy}
        rows={data}
        columns={columns}
        initialState={{
          pinnedColumns: {
            left: ['category'],
          },
        }}
        autoHeight={data.length === 0}
        hideFooter
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableColumnPinning
        disableColumnReorder
      />
    </Box>
  );
}
